import api from './api'

/**
 * Servicio para manejar operaciones relacionadas con cursos
 */
const coursesService = {
    /**
     * Carga cursos desde la API basado en programa, ubicación y mes
     * @param {Object} params - Parámetros para la consulta
     * @param {number|string} params.programId - ID del programa
     * @param {number|string} params.locationId - ID de la ubicación/sede
     * @param {number|string} params.mes - Mes de inicio
     * @returns {Promise<Array>} Array de cursos con sus materias y horarios
     */
    async loadCoursesFromAPI({ programId, locationId, mes }) {
        try {
            const url = `/courses/findbymes/programId?programId=${programId}&locationId=${locationId}&mes=${mes}`
            const { data } = await api.get(url)
            
            if (!data || !Array.isArray(data)) {
                console.error('Los datos de la API no son un array válido:', data)
                return { leftColumn: [], rightColumn: [] }
            }
            
            // Agrupar por materia usando Subject.name
            const materiasMap = {}
            data.forEach((curso, index) => {
                const subjectName = curso.Subject?.name || 'Sin materia'
                if (!materiasMap[subjectName]) {
                    materiasMap[subjectName] = {
                        subjectName,
                        subjectId: curso.Subject?.id,
                        hasMention: curso.Subject?.hasMention || false,
                        courses: [],
                        // Agregar información adicional de la materia
                        totalCourses: 0,
                        availableSeats: 0,
                        totalSeats: 0
                    }
                }
                
                // Agregar información de asientos disponibles
                materiasMap[subjectName].totalCourses++
                materiasMap[subjectName].availableSeats += curso.seatsAvailable || 0
                materiasMap[subjectName].totalSeats += curso.seatsCapacity || 0
                
                materiasMap[subjectName].courses.push(curso)
            })
            
            const materias = Object.values(materiasMap)
            
            // Dividir en columnas izquierda y derecha
            const midPoint = Math.ceil(materias.length / 2)
            return {
                leftColumn: materias.slice(0, midPoint),
                rightColumn: materias.slice(midPoint)
            }
            
        } catch (error) {
            console.error('Error cargando cursos:', error)
            console.error('Detalles del error:', {
                message: error.message,
                response: error.response,
                status: error.response?.status,
                data: error.response?.data
            })
            throw new Error('No se pudieron cargar los cursos.')
        }
    },

    /**
     * Convierte un valor de mes a número
     * @param {string|number} monthValue - Valor del mes (nombre o número)
     * @returns {number} Número del mes (1-12)
     */
    convertMonthToNumber(monthValue) {
        // Si ya es un número, devolverlo
        if (typeof monthValue === 'number') {
            return monthValue
        }
        
        // Si es un string, intentar convertirlo
        if (typeof monthValue === 'string') {
            // Mapeo de nombres de meses a números
            const monthMap = {
                'enero': 1, 'febrero': 2, 'marzo': 3, 'abril': 4,
                'mayo': 5, 'junio': 6, 'julio': 7, 'agosto': 8,
                'septiembre': 9, 'octubre': 10, 'noviembre': 11, 'diciembre': 12
            }
            
            // Buscar el nombre del mes en el string
            const lowerMonth = monthValue.toLowerCase()
            for (const [monthName, monthNumber] of Object.entries(monthMap)) {
                if (lowerMonth.includes(monthName)) {
                    return monthNumber
                }
            }
            
            // Si no se encuentra, intentar extraer solo el número
            const numericMatch = monthValue.match(/\d+/)
            if (numericMatch) {
                const num = parseInt(numericMatch[0])
                if (num >= 1 && num <= 12) {
                    return num
                }
            }
        }
        
        // Valor por defecto si no se puede convertir
        console.warn('No se pudo convertir el mes:', monthValue, 'usando valor por defecto: 4')
        return 4
    },

    /**
     * Obtiene el nombre completo del curso incluyendo información adicional
     * @param {Object} curso - Objeto del curso
     * @returns {string} Nombre completo del curso
     */
    getFullCourseName(curso) {
        const subjectName = curso.Subject?.name || 'Sin materia'
        const courseName = curso.name || ''
        
        // Si el nombre del curso ya incluye el nombre de la materia, usarlo completo
        if (courseName && courseName.includes(subjectName)) {
            return courseName
        }
        
        // Si no, construir el nombre completo
        if (courseName && courseName !== subjectName) {
            return `${subjectName} - ${courseName}`
        }
        
        return subjectName
    },

    /**
     * Obtiene cursos con parámetros por defecto
     * @param {Object} params - Parámetros opcionales
     * @returns {Promise<Object>} Cursos organizados en columnas
     */
    async getCourses(params = {}) {
        const {
            programId,
            locationId,
            mes
        } = params

        const convertedMes = this.convertMonthToNumber(mes)
        
        return await this.loadCoursesFromAPI({
            programId,
            locationId,
            mes: convertedMes
        })
    },

    /**
     * Obtiene cursos desde la API basado en programa, sede y mes
     * @param {Object} params - Parámetros para la consulta
     * @param {number|string} params.programId - ID del programa
     * @param {number|string} params.locationId - ID de la sede
     * @param {number|string} params.mes - Mes de inicio
     * @returns {Promise<Array>} Array de cursos
     */
    async getCoursesRaw({ programId, locationId, mes }) {
        const url = `/courses/findbymes/programId?programId=${programId}&locationId=${locationId}&mes=${mes}`;
        const { data } = await api.get(url);
        return data;
    }
}

export default coursesService 